/**
 * @fileoverview Menu lateral da área de Configurações; no celular, é a lista
 * que abre a área. Mostra só o que o perfil pode ver, agrupado, e marca a seção
 * aberta pela rota. Recolhido (`collapsed`), fica só a coluna dos ícones e o
 * nome de cada item aparece como dica ao passar o mouse.
 *
 * A busca no topo filtra seções e campos (registry.searchSettings): Enter abre o
 * primeiro resultado, Esc limpa. Um campo encontrado abre a seção com
 * #<campo> na URL, e a página rola até ele.
 */

import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { Tooltip } from "@material-ui/core";
import { ChevronRight, PanelLeftClose, PanelLeftOpen, Search, X } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { GROUPS, fieldLabel, searchSettings, sectionPath, visibleItems } from "./registry";
import useSettingsStyles from "./styles";

// Texto da chave ou "" quando ela não existe (a busca não pode indexar a chave crua).
const translate = (key) => i18n.t(key, { defaultValue: "" });

const SettingsNav = ({ ctx, activeId, isMobile, collapsed = false, onToggleCollapsed }) => {
  const classes = useSettingsStyles();
  const history = useHistory();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [focusAfterExpand, setFocusAfterExpand] = useState(false);
  const items = visibleItems(ctx);
  const results = searchSettings(query, ctx, translate);
  // No celular a lista é a página: o título dela é o principal.
  const Title = isMobile ? "h1" : "div";
  const toggleLabel = i18n.t(collapsed ? "settings.area.expandMenu" : "settings.area.collapseMenu");

  // Recolher limpa a busca: a coluna dos ícones não tem onde mostrar resultados.
  useEffect(() => {
    if (collapsed) setQuery("");
  }, [collapsed]);

  // Recolhido, a lupa abre o menu e só então dá para focar o campo.
  useEffect(() => {
    if (!collapsed && focusAfterExpand) {
      if (inputRef.current) inputRef.current.focus();
      setFocusAfterExpand(false);
    }
  }, [collapsed, focusAfterExpand]);

  const openSearch = () => {
    setFocusAfterExpand(true);
    onToggleCollapsed();
  };

  const target = (itemId, fieldId) => ({
    pathname: sectionPath(itemId),
    hash: fieldId ? `#${fieldId}` : "",
    // Vindo da lista do celular, o "voltar" da seção volta para ela.
    state: isMobile ? { fromList: true } : undefined,
  });

  // Escolhido um resultado, o menu volta ao normal (com a seção marcada).
  const afterPick = () => setQuery("");

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      if (query) setQuery("");
      else if (inputRef.current) inputRef.current.blur();
      return;
    }
    // Enter abre o primeiro resultado; se ele lista um campo, o campo (mais exato).
    if (event.key === "Enter" && results && results.length) {
      const first = results[0];
      history.push(target(first.item.id, first.fields.length ? first.fields[0].id : null));
      afterPick();
    }
  };

  const itemLink = ({ id, icon: Icon }, onPick) => {
    const active = id === activeId;
    const label = i18n.t(`settings.sections.${id}.title`);
    return (
      // Recolhido, o nome vira dica; o texto continua no link (leitor de tela).
      <Tooltip key={id} title={collapsed ? label : ""} placement="right">
        <RouterLink
          to={target(id)}
          onClick={onPick}
          className={[classes.navItem, active ? classes.navItemActive : ""].filter(Boolean).join(" ")}
          aria-current={active ? "page" : undefined}
        >
          <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
          <span className={classes.navItemLabel}>{label}</span>
          {isMobile && <ChevronRight size={16} strokeWidth={1.8} aria-hidden="true" />}
        </RouterLink>
      </Tooltip>
    );
  };

  const groupBlock = (group, content) => (
    <div key={group} className={classes.navGroup} role="group" aria-labelledby={`settings-group-${group}`}>
      <div id={`settings-group-${group}`} className={classes.navGroupLabel}>
        {i18n.t(`settings.groups.${group}`)}
      </div>
      {content}
    </div>
  );

  let body;
  if (!results) {
    body = GROUPS.map((group) => {
      const groupItems = items.filter((item) => item.group === group);
      return groupItems.length ? groupBlock(group, groupItems.map((item) => itemLink(item))) : null;
    });
  } else if (results.length) {
    body = GROUPS.map((group) => {
      const groupResults = results.filter((result) => result.item.group === group);
      if (!groupResults.length) return null;
      return groupBlock(
        group,
        groupResults.map(({ item, fields }) => (
          <React.Fragment key={item.id}>
            {itemLink(item, afterPick)}
            {fields.map((field) => (
              <RouterLink
                key={field.id}
                to={target(item.id, field.id)}
                onClick={afterPick}
                className={classes.navSubItem}
              >
                {fieldLabel(item.id, field.id, translate)}
              </RouterLink>
            ))}
          </React.Fragment>
        ))
      );
    });
  } else {
    body = (
      <div className={classes.searchEmpty}>
        <p>{i18n.t("settings.search.empty", { query, interpolation: { skipOnVariables: true } })}</p>
        <p>{i18n.t("settings.search.emptyHint")}</p>
      </div>
    );
  }

  return (
    <nav className={classes.nav} aria-label={i18n.t("settings.title")}>
      <div className={classes.navHeader}>
        <Title className={classes.navTitle}>{i18n.t("settings.title")}</Title>
        {onToggleCollapsed && (
          <button
            type="button"
            className={classes.iconButton}
            onClick={onToggleCollapsed}
            aria-label={toggleLabel}
            aria-expanded={!collapsed}
            title={toggleLabel}
          >
            {collapsed ? <PanelLeftOpen size={16} strokeWidth={1.8} /> : <PanelLeftClose size={16} strokeWidth={1.8} />}
          </button>
        )}
      </div>

      <div className={classes.navSearch} role="search">
        <Search size={14} strokeWidth={1.8} aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          className={classes.navSearchInput}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={i18n.t("settings.search.placeholder")}
          aria-label={i18n.t("settings.search.label")}
          tabIndex={collapsed ? -1 : 0}
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            type="button"
            className={classes.navSearchClear}
            onClick={() => {
              setQuery("");
              if (inputRef.current) inputRef.current.focus();
            }}
            aria-label={i18n.t("settings.search.clear")}
            title={i18n.t("settings.search.clear")}
          >
            <X size={14} strokeWidth={1.8} />
          </button>
        )}
        {collapsed && (
          <Tooltip title={i18n.t("settings.search.open")} placement="right">
            <button
              type="button"
              className={classes.navSearchOpen}
              onClick={openSearch}
              aria-label={i18n.t("settings.search.label")}
            />
          </Tooltip>
        )}
      </div>

      <div className={classes.srOnly} aria-live="polite">
        {results
          ? i18n.t("settings.search.count", {
              count: results.reduce((total, result) => total + 1 + result.fields.length, 0),
            })
          : ""}
      </div>

      {body}
    </nav>
  );
};

export default SettingsNav;
